/**
 * 상류 서비스 상태 API 클라이언트 — S34 Phase 1(읽기 전용) + Phase 2(DB 설정).
 *
 * 계약 정본은 서버(server/upstreams.py §2.7)다. 이 파일은 그 응답 모양을
 * 문자 그대로 타입에 옮긴다 — 여기 선언 안 된 필드는 조용히 undefined 가
 * 되고, 그걸 계약이 깨진 채로 놔두는 사고를 test_inventory 가 잡는다.
 *
 * Phase 2: DB 에 저장한 override 로 상류 URL·모델을 바꾼다(PUT), env 로
 * 되돌린다(DELETE), 저장 전에 시험한다(POST .../test) — §2.2/§2.4.
 */

import { API_BASE } from './config'
import { ApiError } from './api'
import { accountAuthHeaders } from './token'

/** 서버 UPSTREAM_SERVICES 와 문자열 집합이 같아야 한다(§2.1). */
export type UpstreamId = 'omni' | 'whisper' | 'asr' | 'correct' | 'multiear_ear3'

/**
 * 표시 전용(편집 불가) 서비스 — server SERVICE_FIELDS(server/upstreams.py)
 * 와 문자열 집합이 같아야 한다(DEF-050 AMENDMENT #1 A2). `multiear_ear3` 는
 * 시딩·DB override 대상이 아니라 PUT/DELETE 가 `read_only_service` 로
 * 거절한다 — UI 도 편집·시험 버튼을 아예 안 보여준다(AMENDMENT #2 B3).
 */
export const READ_ONLY_UPSTREAM_IDS: ReadonlySet<UpstreamId> = new Set(['multiear_ear3'])

/** 서버 §2.4 상태 어휘 — 닫힌 집합. 빠진 값은 UI 에서 무배지=정상처럼 보인다. */
export type UpstreamStatus = 'ok' | 'degraded' | 'starting' | 'down' | 'disabled' | 'unknown'

/** 상류 서비스 1개의 프로브 결과 — §2.7 필드 전부. */
export interface UpstreamServiceStatus {
  id: UpstreamId
  label: string
  status: UpstreamStatus
  enabled: boolean
  /** enabled=false 일 때 어떤 스위치가 껐는지(예: TRANS_STT_DUAL=0). enabled 면 빈 문자열 */
  disabled_reason: string
  url: string
  probe_url: string
  /** 이 서비스의 유효 설정 모델명 */
  model: string
  /** `/v1/models` data[].id 전체 */
  models_seen: string[]
  /** `/health` 의 model 필드(HF 리포명) — 참고 정보. 실패·빈 바디면 null */
  health_detail: string | null
  /** 실패 시 null */
  latency_ms: number | null
  /** 실패 원문 요약, 200자 절단 */
  detail: string
  timeout_s: number
  /** 이 값이 DB override 에서 왔는가, env 그대로인가(§1.5) */
  source: 'db' | 'env'
  /** 이 프로세스의 현재 env 값(URL) — override 와 별개로 항상 채워진다 */
  env_url: string
  /** 이 프로세스의 현재 env 값(모델) */
  env_model: string
  /** 유효값(url)이 지금의 env_url 과 다른가 — 3단 드리프트 판정의 재료(§3, URL 축 유지) */
  env_drift: boolean
  /** F-C2 — env 의 모델명과 유효 모델이 다른가(§3, model_drift). URL 축과 별개다. */
  model_drift: boolean
  /** 최초 seed 당시의 env url — env_url 이 그 뒤로 또 바뀌었는지 비교용 */
  seeded_env: string
  /** F-C1 — 저장은 됐으나 아직 재기동 전이라 실제 트래픽은 옛 값을 쓴다(§3, omni 축). */
  pending_restart: boolean
  /** 마지막으로 이 override 를 저장한 관리자. '' 면 seed 값 그대로(관리자가 손댄 적 없음) */
  edited_by: string
  /** override 마지막 갱신 시각(epoch 초) */
  updated_at: number
}

/** `GET /api/admin/upstreams` 응답 — §2.7. */
export interface UpstreamsResponse {
  checked_at: number
  cached: boolean
  age_s: number
  probe_ttl_s: number
  meeting_live: boolean
  services: UpstreamServiceStatus[]
}

/**
 * 상류 상태 조회 — stream 서비스 라우트(ROLE_STREAM, §2.6). 관리자 전용,
 * 비관리자는 403(ApiError) — 화면은 그걸 조용한 빈 상태로 받는다.
 */
export async function fetchUpstreams(force = false): Promise<UpstreamsResponse> {
  const res = await fetch(`${API_BASE}/admin/upstreams?force=${force ? 1 : 0}`, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(await accountAuthHeaders()),
    },
  })
  const text = await res.text()
  let body: unknown = {}
  try {
    body = text ? JSON.parse(text) : {}
  } catch {
    body = {}
  }
  if (!res.ok) {
    const rec = (body ?? {}) as { detail?: unknown; error?: unknown }
    const code =
      typeof rec.detail === 'string'
        ? rec.detail
        : typeof rec.error === 'string'
          ? rec.error
          : 'unknown'
    throw new ApiError(res.status, code, '상류 상태를 불러오지 못했습니다.')
  }
  return body as UpstreamsResponse
}

/** PUT/DELETE 저장·되돌리기 응답 — §2.4. */
export interface SaveUpstreamResponse {
  ok: true
  service: UpstreamId
  /** "next_room" 이면 진행 중인 방은 기존 값 유지, "restart_required" 면 재기동 전까지 미적용(omni 는 항상 이 값) */
  applies: 'next_room' | 'restart_required'
  restart_required: boolean
  /** 이 값이 아직 적용 안 된 채 진행 중인 방 수 */
  live_rooms: number
  /** 서버가 계산한 안내문 — 토스트에 그대로 붙인다(값을 UI 가 다시 지어내지 않는다) */
  note: string
}

/** POST .../test 응답 — 저장 없이 프로브만. 기존 프로브 결과 모양을 재사용한다. */
export interface TestUpstreamResponse {
  status: UpstreamStatus
  latency_ms: number | null
  models_seen: string[]
  health_detail: string | null
  detail: string
}

/** PUT/POST 검증 실패 코드 → 한국어 안내(§2.2). */
const VALIDATION_MESSAGES: Record<string, string> = {
  empty_value: '주소·모델명을 입력해 주세요.',
  bad_scheme: '주소는 http:// 또는 https:// 로 시작해야 합니다.',
  bad_host: '올바른 호스트가 아닙니다.',
  public_host: '공개 호스트는 허용되지 않습니다(사설망만 가능).',
  link_local_host: '링크로컬 주소는 허용되지 않습니다(클라우드 메타데이터 등).',
  bad_path: '이 서비스에 맞는 경로가 아닙니다.',
  too_long: '주소 또는 모델명이 너무 깁니다.',
  bad_model: '모델명 형식이 올바르지 않습니다.',
  read_only_service: '이 서비스는 표시 전용입니다(env 로만 변경할 수 있습니다).',
}

/** fetchUpstreams 와 같은 패턴의 요청 도우미 — 방어적 JSON 파싱, 코드→한국어 매핑. */
async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(await accountAuthHeaders()),
      ...(init?.headers ?? {}),
    },
  })
  const text = await res.text()
  let body: unknown = {}
  try {
    body = text ? JSON.parse(text) : {}
  } catch {
    body = {}
  }
  if (!res.ok) {
    const rec = (body ?? {}) as { detail?: unknown; error?: unknown }
    const code =
      typeof rec.detail === 'string'
        ? rec.detail
        : typeof rec.error === 'string'
          ? rec.error
          : 'unknown'
    throw new ApiError(res.status, code, VALIDATION_MESSAGES[code] ?? '요청이 실패했습니다.')
  }
  return body as T
}

/** DB override 저장(PUT) — §2.2. */
export function saveUpstream(
  id: UpstreamId,
  url: string,
  model: string,
): Promise<SaveUpstreamResponse> {
  return req(`/admin/upstreams/config/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ url, model }),
  })
}

/** DB override 를 지우고 env 값으로 되돌린다(DELETE, reseed) — §2.2. */
export function resetUpstream(id: UpstreamId): Promise<SaveUpstreamResponse> {
  return req(`/admin/upstreams/config/${id}`, { method: 'DELETE' })
}

/** 저장 없이 값만 시험한다(POST .../test) — §2.2. */
export function testUpstream(
  id: UpstreamId,
  url: string,
  model: string,
): Promise<TestUpstreamResponse> {
  return req(`/admin/upstreams/${id}/test`, {
    method: 'POST',
    body: JSON.stringify({ url, model }),
  })
}
